import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from '@uhg-harmony/uitk-react';
import { IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getFilteredContainersAndIndices,
  mapContainersToPaginatedRows,
} from './RepeaterUtils';
import { useDispatch } from 'react-redux';
import { updateWidgetConfig } from 'shared/state/ducks/sites';
import { previewSelectedFilters } from './RepeaterSidebarPlugin';

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  width: 100%;
  position: relative;
  z-index: 1001;
  box-sizing: border-box;
`;

const PaginationButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationActionButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TextWrapper = styled.span<{ isBold: boolean; reducePadding?: boolean }>`
  font-family: 'OptumSans';
  font-weight: ${props => (props.isBold ? '600' : '400')};
  padding: ${props => (props.reducePadding ? '0 0.25rem' : '0 0.5rem')};
  font-size: 14px;
`;

const ActionButton = styled(Button)<{ isPreviousButton: boolean }>`
  font-family: 'OptumSans';
  font-size: 14px;
  min-width: 80px;
  height: 36px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface RepeaterPaginationProps {
  currentPage?: any;
  totalPages?: number;
  itemsPerPage?: number;
  totalItems?: number;
  canPreviousPage?: boolean;
  canNextPage?: boolean;
  onPageChange?: (page: number) => void;
  widgetState?: any;
  siteId?: number;
}

/**
 * Pagination component for RepeaterWidget containers
 * @param props - Pagination properties
 * @returns JSX.Element
 */
const RepeaterPagination: React.FC<RepeaterPaginationProps> = ({
  currentPage,
  widgetState,
  siteId,
}) => {
  const dispatch = useDispatch();

  // Initialize from widget config
  const [currentPaginationPage, setCurrentPaginationPage] = useState(
    widgetState?.config?.currentPaginationPage || 1
  );

  // Update local state when widget config changes
  useEffect(() => {
    const configPage = widgetState?.config?.currentPaginationPage || 1;
    if (configPage !== currentPaginationPage) {
      setCurrentPaginationPage(configPage);
    }
  }, [widgetState?.config?.currentPaginationPage]);

  let totalItems = widgetState?.config?.repeaterData?.data?.length || 0;

  // When filtering is enabled, calculate total based on filtered data count
  if (widgetState?.config?.enableFiltering && widgetState?.config?.filterBy) {
    const { previewSelectedFilters } = require('./RepeaterSidebarPlugin');
    const selectedOptions = previewSelectedFilters[widgetState.id];

    if (selectedOptions) {
      const { getFilteredContainersAndIndices } = require('./RepeaterUtils');
      const result = getFilteredContainersAndIndices(
        widgetState.config.repeaterData,
        widgetState.config.filterBy,
        selectedOptions,
        widgetState.children || [],
        currentPage
      );
      totalItems = result.filteredDataIndices.length;
    }
  }

  const itemsPerPage = widgetState?.config?.paginatedItemsPerPage || 9;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const canPreviousPage = currentPaginationPage > 1;
  const canNextPage = currentPaginationPage < totalPages;

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      if (
        pageNumber === currentPaginationPage ||
        pageNumber < 1 ||
        pageNumber > totalPages
      ) {
        return;
      }

      // Update widget config with new page number
      dispatch(
        updateWidgetConfig(
          siteId,
          currentPage?.pageId,
          widgetState.id,
          {
            ...widgetState.config,
            currentPaginationPage: pageNumber,
          },
          false
        )
      );

      // Check if filtering is enabled and get filtered containers
      // When both filtering and pagination are enabled, we need to paginate ONLY the filtered containers
      let filteredContainerIds: string[] | undefined;
      let filteredDataIndices: number[] | undefined;

      if (widgetState.config.enableFiltering && widgetState.config.filterBy) {
        // Get the current filter selections
        const selectedOptions = previewSelectedFilters[widgetState.id];

        if (selectedOptions) {
          const result = getFilteredContainersAndIndices(
            widgetState.config.repeaterData,
            widgetState.config.filterBy,
            selectedOptions,
            widgetState.children || [],
            currentPage
          );
          filteredContainerIds = result.filteredContainerIds;
          filteredDataIndices = result.filteredDataIndices;
        }
      }

      // Update container visibility, passing filtered containers if filtering is active
      mapContainersToPaginatedRows(
        widgetState.id,
        widgetState.config.enablePagination,
        currentPage,
        widgetState.config.repeaterData,
        widgetState.config.paginatedItemsPerPage,
        dispatch,
        siteId,
        currentPage.pageId,
        pageNumber,
        filteredContainerIds,
        filteredDataIndices
      );
    },
    [
      currentPaginationPage,
      totalPages,
      widgetState.id,
      widgetState.config,
      widgetState.children,
      currentPage,
      siteId,
      dispatch,
    ]
  );

  const handlePreviousPage = useCallback(() => {
    if (canPreviousPage) {
      const newPage = currentPaginationPage - 1;
      handlePageChange(newPage);
    }
  }, [canPreviousPage, currentPaginationPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (canNextPage) {
      const newPage = currentPaginationPage + 1;
      handlePageChange(newPage);
    }
  }, [canNextPage, currentPaginationPage, handlePageChange]);

  if (!widgetState?.config?.enablePagination || totalPages < 1) {
    return null;
  }

  return (
    <PaginationWrapper data-test-id="repeater-pagination-wrapper">
      <PaginationButtonsWrapper>
        <TextWrapper isBold={true} reducePadding={true}>
          Items per page
        </TextWrapper>
        <Tooltip
          title="Select to alter items per page"
          arrow
          placement="top-start"
        >
          <IconButton size="small" data-test-id="pagination-info-icon">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <TextWrapper isBold={false} reducePadding={true}>
          {itemsPerPage}
        </TextWrapper>
      </PaginationButtonsWrapper>

      <PaginationActionButtonsWrapper>
        <TextWrapper isBold={true} reducePadding={true}>
          Displaying page: {currentPaginationPage} of {totalPages}
        </TextWrapper>
        <ActionButton
          variant="tertiary"
          onPress={handlePreviousPage}
          disabled={!canPreviousPage}
          isPreviousButton={true}
          data-test-id="pagination-previous-button"
        >
          Previous
        </ActionButton>
        <ActionButton
          variant="tertiary"
          onPress={handleNextPage}
          disabled={!canNextPage}
          isPreviousButton={false}
          data-test-id="pagination-next-button"
        >
          Next
        </ActionButton>
      </PaginationActionButtonsWrapper>
    </PaginationWrapper>
  );
};

export default RepeaterPagination;
